import type { Font } from 'opentype.js';
import type { DrawingDocument, TextRun } from '../model';
import { DEFAULT_TEXT_FONT_FAMILY, type TextFont, type TextMeasure } from './textLayout';

// #757 -- outlined SVG text (docs/model/drawing-ui-export.md § Delivery decisions for #757).
// Exported text becomes vector paths drawn from bundled, metric-compatible open fonts, so the file
// looks the same everywhere. Fonts load lazily, only when exporting.

export type ExportFontFamily = 'Arimo' | 'Tinos' | 'Cousine' | 'Gelasio';
export type ExportFontKey = `${ExportFontFamily}-${'Regular' | 'Bold' | 'Italic' | 'BoldItalic'}`;

// Authored CSS family lists map to the bundled stand-in by their first family. Unknown families
// fall back to Arimo and are reported as substitutions.
const STAND_INS: readonly [readonly string[], ExportFontFamily][] = [
  [['arial', 'helvetica', 'arimo', 'liberation sans', 'sans-serif', 'system-ui'], 'Arimo'],
  [['times new roman', 'times', 'tinos', 'liberation serif', 'serif'], 'Tinos'],
  [['courier new', 'courier', 'cousine', 'liberation mono', 'monospace'], 'Cousine'],
  [['georgia', 'gelasio'], 'Gelasio']
];

export function exportFontFamily(cssFamily: string): { readonly family: ExportFontFamily; readonly known: boolean } {
  const first = (cssFamily.split(',')[0] ?? '')
    .trim()
    .replace(/^["']|["']$/g, '')
    .toLowerCase();
  const match = STAND_INS.find(([names]) => names.includes(first));
  return match ? { family: match[1], known: true } : { family: 'Arimo', known: false };
}

export function exportFontKey(font: Pick<TextFont, 'fontFamily' | 'bold' | 'italic'>): ExportFontKey {
  const { family } = exportFontFamily(font.fontFamily);
  const style = font.bold ? (font.italic ? 'BoldItalic' : 'Bold') : font.italic ? 'Italic' : 'Regular';
  return `${family}-${style}`;
}

const runFont = (run: TextRun) => ({
  fontFamily: run.fontFamily ?? DEFAULT_TEXT_FONT_FAMILY,
  bold: run.bold ?? false,
  italic: run.italic ?? false
});

function textRuns(document: DrawingDocument): { readonly annotationId: string; readonly run: TextRun }[] {
  return document.annotations.flatMap((annotation) =>
    annotation.kind === 'text'
      ? annotation.paragraphs.flatMap((paragraph) =>
          paragraph.runs.map((run) => ({ annotationId: annotation.id, run }))
        )
      : []
  );
}

// #792 -- chemical labels (atom symbols, implicit H, isotopes, charges, radicals, Markush and
// repeat-unit labels) are outlined in Arimo Bold: on screen they are Arial at weight 600/800,
// which Arial (and Arimo) only has as Bold, so Bold is exactly what users see (owner, Oct 5).
export const LABEL_FONT_KEY: ExportFontKey = 'Arimo-Bold';

// The font files an export of `document` needs.
export function requiredExportFonts(document: DrawingDocument): ReadonlySet<ExportFontKey> {
  const keys = new Set(textRuns(document).map(({ run }) => exportFontKey(runFont(run))));
  if (document.structure.graph.nodes.length > 0) keys.add(LABEL_FONT_KEY);
  return keys;
}

export interface ExportFonts {
  readonly font: (key: ExportFontKey) => Font | undefined;
}

// Loads the given fonts from `baseUrl` (the app serves them from /fonts/export/). opentype.js
// itself is imported here, on demand, so it never weighs on the editor.
export async function loadExportFonts(
  keys: Iterable<ExportFontKey>,
  baseUrl: string,
  fetchFont: (url: string) => Promise<ArrayBuffer> = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Could not load export font ${url} (${response.status})`);
    return response.arrayBuffer();
  }
): Promise<ExportFonts> {
  // opentype.js is CommonJS: bundlers expose `parse` directly, Node's loader only on the default
  // export (Playwright's #792 label test imports this module from Node).
  const opentype = (await import('opentype.js')) as typeof import('opentype.js') & {
    readonly default?: typeof import('opentype.js');
  };
  const parse = opentype.parse ?? opentype.default!.parse;
  const loaded = new Map<ExportFontKey, Font>();
  await Promise.all(
    [...new Set(keys)].map(async (key) => {
      loaded.set(key, parse(await fetchFont(`${baseUrl.replace(/\/$/, '')}/${key}.ttf`)));
    })
  );
  return { font: (key) => loaded.get(key) };
}

const KERNING = { kerning: true } as const;

// Text widths from the export fonts themselves, so layout (alignment, bounds) matches the outlines.
export function exportFontMeasure(fonts: ExportFonts, fallback: TextMeasure): TextMeasure {
  return (text, font) => {
    const outlineFont = fonts.font(exportFontKey(font));
    return outlineFont ? outlineFont.getAdvanceWidth(text, font.fontSizePx, KERNING) : fallback(text, font);
  };
}

// Path data for `text` with its baseline starting at (x, y), or null when the font lacks any of its
// characters (that span is then kept as editable text).
export function outlinePath(fonts: ExportFonts, text: string, font: TextFont, x: number, y: number): string | null {
  const outlineFont = fonts.font(exportFontKey(font));
  if (!outlineFont) return null;
  for (const character of text) if (!/\s/.test(character) && !outlineFont.hasChar(character)) return null;
  return outlineFont.getPath(text, x, y, font.fontSizePx, KERNING).toPathData(2);
}

// #792 -- path data for a chemical label positioned as the live label is: anchored at x ('middle'
// centres it, 'start' begins there) and vertically centred on y like dominant-baseline="central"
// -- the baseline sits half the font's ascent-plus-descent below the centre line (Arimo shares
// Arial's metrics, so this matches the browser). Null when the font lacks a character (the label
// then stays live text).
export function outlineLabel(
  fonts: ExportFonts,
  text: string,
  fontSizePx: number,
  x: number,
  y: number,
  anchor: 'middle' | 'start' = 'middle'
): string | null {
  const font = fonts.font(LABEL_FONT_KEY);
  if (!font || text.length === 0) return null;
  for (const character of text) if (!/\s/.test(character) && !font.hasChar(character)) return null;
  const width = font.getAdvanceWidth(text, fontSizePx, KERNING);
  const left = anchor === 'middle' ? x - width / 2 : x;
  const baseline = y + ((font.ascender + font.descender) / 2 / font.unitsPerEm) * fontSizePx;
  return font.getPath(text, left, baseline, fontSizePx, KERNING).toPathData(2);
}

export interface TextExportReport {
  // Characters the export fonts cannot draw, per text annotation; those runs stay editable text.
  readonly missing: readonly { readonly annotationId: string; readonly characters: readonly string[] }[];
  // Authored font families with no bundled stand-in, and what draws them instead.
  readonly substitutions: readonly { readonly from: string; readonly to: ExportFontFamily }[];
}

// What an outlined export will change, identified before exporting (#610).
export function textExportReport(document: DrawingDocument, fonts: ExportFonts): TextExportReport {
  const missing = new Map<string, Set<string>>();
  const substitutions = new Map<string, ExportFontFamily>();
  for (const { annotationId, run } of textRuns(document)) {
    const font = runFont(run);
    const { family, known } = exportFontFamily(font.fontFamily);
    if (!known) substitutions.set(font.fontFamily, family);
    const outlineFont = fonts.font(exportFontKey(font));
    for (const character of run.text) {
      if (/\s/.test(character) || outlineFont?.hasChar(character)) continue;
      const set = missing.get(annotationId) ?? new Set<string>();
      set.add(character);
      missing.set(annotationId, set);
    }
  }
  return {
    missing: [...missing].map(([annotationId, characters]) => ({ annotationId, characters: [...characters] })),
    substitutions: [...substitutions].map(([from, to]) => ({ from, to }))
  };
}
